import React from 'react'
import { get } from 'lodash'
import { gtmEvent } from 'lib/gtm'
import { useAuth } from 'lib/auth'
import Container from '@material-ui/core/Container'
import Grid from '@material-ui/core/Grid'
import Typography from '@material-ui/core/Typography'
import Divider from '@material-ui/core/Divider'
import Avatar from '@material-ui/core/Avatar'
import Button from '@material-ui/core/Button'
import TransactionSummary from './transaction-summary'
import { calculateStripeFee } from 'lib/stripe'
import StepDescription from '../step-description'
import { useStyles } from './styles'
import { flags, useFeatureFlags } from 'lib/feature-flags'
import { DownloadMyPGAsection } from 'components/download-mypga-section'
import BookingTypeCard from 'components/booking-type-card'
import SummaryLayoutRefresh from '../ReviewBookingNewRefresh/summary-layout-refresh'

const LessonReviewLayout = ({
  idOrSlug,
  lessonType,
  lessonTypeId,
  locationId,
  startDateTime,
  student,
  cardBrand,
  lastDigits,
  isComplete,
  coachIsViewingOwnSchedule,
}) => {
  const {
    title,
    category,
    priceInCents,
    durationInMinutes,
    coach,
    facilities,
    acceptsOnlinePayments,
  } = lessonType
  const facility = facilities.find(({ id }) => id === locationId)

  const { isCoach } = useAuth()
  const paymentRequired = !isCoach && acceptsOnlinePayments

  const studentName = `${student.firstName} ${student.lastName}`
  const [
    isStripeFlatFeeDeducted,
    isCoachOfferingRefreshEnabled,
  ] = useFeatureFlags([
    flags.FLAT_FEAT_STRIPE_FEE_DEDUCTED,
    flags.FLAG_FEAT_COACH_OFFERING_REFRESH,
  ])

  const processingFeeInDollars = isStripeFlatFeeDeducted
    ? 0
    : calculateStripeFee(priceInCents || 0) / 100

  const classes = useStyles()
  const bookNewSessionUrl = `/coach/${idOrSlug}/schedule?lessonTypeId=${lessonTypeId}&locationId=${locationId}`

  const onBookAnotherSession = () => {
    gtmEvent({
      event: 'formSubmit',
      formCategory: 'book-session',
      formAction: 'clicked-book-another-session-button',
    })
    window.open(bookNewSessionUrl, '_blank')
  }

  const onViewCoachOfferings = () => {
    gtmEvent({
      event: 'formSubmit',
      formCategory: 'book-session',
      formAction: 'clicked-view-coachofferings-button',
    })
    window.location.href = window.location.pathname
  }

  return (
    <>
      {isCoachOfferingRefreshEnabled ? (
        <SummaryLayoutRefresh
          idOrSlug={idOrSlug}
          lessonType={lessonType}
          lessonTypeId={lessonTypeId}
          locationId={locationId}
          startDateTime={startDateTime}
          student={student}
          cardBrand={cardBrand}
          lastDigits={lastDigits}
          isComplete={isComplete}
          coachIsViewingOwnSchedule={coachIsViewingOwnSchedule}
        />
      ) : (
        <Container className={classes.root}>
          <Grid container>
            <Grid item md={7} xs={12}>
              <StepDescription
                stepText=""
                title="Thank you!"
                description="Your booking is complete. You will receive a confirmation email shortly."
              />
              {!coachIsViewingOwnSchedule ||
                (paymentRequired && (
                  <TransactionSummary
                    title={title}
                    priceInCents={priceInCents}
                    processingFeeInDollars={processingFeeInDollars}
                    cardBrand={cardBrand}
                    lastDigits={lastDigits}
                  />
                ))}
              <div className={classes.rowWrapped}>
                <Avatar src={get(coach, 'coachProfile.profilePhoto')}>
                  {coach.name.substring(0, 1)}
                </Avatar>
                <div className={classes.infoWrapped}>
                  <Typography
                    variant="caption"
                    className={`${classes.fontBolded} ${classes.captionText}`}
                  >
                    {coach.name}
                  </Typography>
                  <Typography
                    variant="caption"
                    className={`${classes.captionText} ${classes.lighterText}`}
                  >
                    {get(coach, 'coachProfile.title', '')}
                  </Typography>
                </div>
              </div>
              <BookingTypeCard
                category={category}
                facility={facility}
                title={title}
                startDateTime={startDateTime}
                durationInMinutes={durationInMinutes}
                isComplete={isComplete}
              />
              <div className={classes.rowWrapped}>
                <Avatar alt={studentName}>
                  {`${student.firstName.substring(
                    0,
                    1,
                  )}${student.lastName.substring(0, 1)}`}
                </Avatar>
                <div className={classes.infoWrapped}>
                  <Typography
                    variant="caption"
                    className={`${classes.fontBolded} ${classes.captionText}`}
                  >
                    {studentName}
                  </Typography>
                  <Typography
                    variant="caption"
                    className={`${classes.captionText} ${classes.lighterText}`}
                  >
                    Student
                  </Typography>
                </div>
              </div>
              <Divider className={classes.dividerStyles} />
              <Grid container spacing={2}>
                <Grid item md={6} xs={12}>
                  <Button
                    color="primary"
                    variant="outlined"
                    size="large"
                    fullWidth
                    onClick={onViewCoachOfferings}
                  >
                    {!coachIsViewingOwnSchedule
                      ? 'VIEW COACH OFFERINGS'
                      : 'VIEW MY SCHEDULE'}
                  </Button>
                </Grid>
                <Grid item md={6} xs={12}>
                  <Button
                    variant="contained"
                    color="primary.light"
                    size="large"
                    fullWidth
                    onClick={onBookAnotherSession}
                  >
                    BOOK ANOTHER SESSION
                  </Button>
                </Grid>
              </Grid>
              <DownloadMyPGAsection />
            </Grid>
          </Grid>
        </Container>
      )}
    </>
  )
}

export default LessonReviewLayout
